import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

import {
  FiArrowLeft, FiArrowRight, FiMessageCircle, FiX,
} from 'react-icons/fi';
import { useQuery } from 'react-query';
import { BasketCard, Image } from '../../components';
import BasketWithoutProducts from './BasketWithoutProducts'
import { OfferService, SpaceService } from '../../../support/services/api';
import './styles.css';
import { removeFromBasket } from '../../../support/store/BasketReducer';
import useBasket from '../../../model/useBasket';

function Basket({
  basketItems = [], producerId = '', dispatch, history,
}) {
  const [offers, setOffers] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [productsCounter, setProductsCounter] = useState(0);
  const [invalidBasket, setInvalidBasket] = useState(false);
  const [invalidAmount, setInvalidAmount] = useState(false);
  const [hasDifferentTypes, setHasDifferentTypes] = useState(false);

  const { sharedStates: basketStates } = useBasket();

  const { data: space = {} } = useQuery(
    ['spaces', { id: basketStates.spaceId }],
    () => SpaceService().getByIds([basketStates.spaceId]).then((res) => res.producers[0]),
    {
      keepPreviousData: true,
      enabled: !!basketStates.spaceId,
    },
  );

  const producerImage = useMemo(() => space.imagesPaths?.[space.imagesPaths?.length - 1], [space]);

  function sendWhatsappMessage() {
    const message = `Olá, ${space.name}. Gostaria de conversar você a respeito de algumas de suas ofertas.`;
    window.open(`https://api.whatsapp.com/send?phone=${space.whatsapp}&text=%20${message}`);
  }

  const checkBasketValidity = useCallback(() => {
    const basketItemsIds = basketItems.map((basketItem) => basketItem.id);
    const invalidItems = offers.filter((offer) => offer.availableStock <= 0 && basketItemsIds.includes(offer.id) && offer.type !== 'scheduled_delivery');

    setInvalidBasket(invalidItems.length > 0);
  }, [basketItems, offers]);

  useEffect(() => {
    checkBasketValidity();
  }, [basketItems, checkBasketValidity]);

  useEffect(() => {
    if (basketItems.length > 0) {
      const ids = basketItems.map((basketItem) => basketItem.id);

      OfferService().getByIds(ids)
        .then((response) => {
          const offersData = response.offers;

          if (basketItems.length !== offersData.length) {
            const offersIds = offersData.map((offer) => offer.id);
            const deletedOffers = ids.filter((id) => !offersIds.includes(id));
            deletedOffers.forEach((id) => dispatch(removeFromBasket({ id })));

            return;
          }

          let total = 0;
          let count = 0;

          setOffers(offersData);

          offersData.forEach((offer) => {
            const basketItemAmount = basketItems
              .filter((basketItem) => basketItem.id === offer.id)[0].amount;

            total += offer.value * basketItemAmount;
            count += basketItemAmount;
          });

          setProductsCounter(count);
          setTotalValue(total);

          const OffersTypes = offersData.map((offer) => offer.type);
          setHasDifferentTypes(OffersTypes.find((i) => i === 'scheduled_delivery') && OffersTypes.find((i) => i === 'prompt_delivery'));
        });
    }
  }, [producerId, basketItems, dispatch]);

  const handleNext = useCallback(() => {
    if (invalidAmount) {
      toast(`Para completar um pacote, faltam ${25 - productsCounter % 25} unidades`, { type: toast.TYPE.INFO });
      return;
    }
    history.push('/login', {
      shouldConfirm: true,
      nextPath: '/checkout',
    });
  }, [productsCounter, invalidAmount, history]);

  useEffect(() => {
    setInvalidAmount(space.nickname === 'fragus' && productsCounter % 25 !== 0);
  }, [productsCounter, space]);

  if(!basketItems.length) return <BasketWithoutProducts />

  return (
    <div className="basket-container">
      <section className="header-container" style={{ boxShadow: 'none' }}>
        <div className="navigation-container">
          <FiArrowLeft className="touchable-icon" size={32} color="#000" onClick={history.goBack} />

          {basketItems.length ? (<p style={{ fontSize: '18px', fontWeight: 200 }}>Sua cesta com produtos de</p>) : (<p>Sua cesta está vazia</p>)}

          {
            basketItems.length > 0
            && <FiMessageCircle onClick={sendWhatsappMessage} size={32} color="#000" className="share-icon" />
          }
        </div>

        {
          basketItems.length && space
            ? (
              <section className="producer-container">
                <div className="producer-info">
                  <Image src={producerImage} alt={`Foto de ${space?.firstName} ${space?.lastName}`} rounded />

                  <div className="producer-text">
                    <h2>
                      @
                      {space?.nickname}
                    </h2>
                    <p>{space?.name}</p>
                  </div>
                </div>
              </section>
            )
            : (<></>)
        }
      </section>

      {
        offers.length ? (
          <div>
            <section className="basket-items-container">
              {
                basketItems.map((basketItem) => {
                  const offer = offers.filter((item) => basketItem.id === item.id)[0];

                  if (!offer) return (<></>);

                  return (
                    <BasketCard
                      key={offer.id}
                      offer={offer}
                      basketItem={basketItem}
                    />
                  );
                })
              }
            </section>

            {
              basketItems.length > 0
              && (
                <section className="summary-container">

                  {invalidAmount && <SummaryAlert text="Esse produtor permite pacotes personalizados. Por isso, é necessário que a quantidade de produtos seja multipla de 25." />}

                  {hasDifferentTypes && <SummaryAlert text="Sua cesta possui produtos pronta entrega e encomenda. As datas disponíveis para entrega/retirada serão referentes às da encomenda." />}

                  <div className="summary-text">
                    <div className="summary-subtitles">
                      <p>{`Produtos (${productsCounter})`}</p>
                      <p>
                        Frete
                        <span className="summary-free-freight">
                          {
                            totalValue < space?.delivery?.homeDelivery?.gratuity?.minOrderValue
                        && ` (Grátis acima de R$ ${space.delivery.homeDelivery.gratuity.minOrderValue})`
                          }
                        </span>
                      </p>
                      <h2>Total</h2>
                    </div>

                    <div className="summary-values">
                      <p>
                        {Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalValue)}
                      </p>
                      <p>
                        {
                          totalValue >= space?.delivery?.homeDelivery?.gratuity?.minOrderValue
                            ? 'Grátis'
                            : 'A definir'
                        }
                      </p>
                      <h2>
                        {Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalValue)}
                      </h2>
                    </div>
                  </div>

                  <div className="basket-bar-content">
                    {
                      invalidBasket
                        ? (
                          <div className="basket-bar" style={{ backgroundColor: '#c50000' }}>
                            <FiX size={15} color="#FFF" strokeWidth="1" />
                            <p className="basket-bar-text">Exclua ofertas sem estoque para prosseguir</p>
                          </div>
                        )
                        : (
                          <button type="button" onClick={handleNext} className="basket-bar">
                            <FiArrowRight size={15} color="#FFF" strokeWidth="1" />
                            <p className="basket-bar-text">Prosseguir com o pedido</p>
                          </button>
                        )
                    }
                  </div>
                </section>
              )
            }
          </div>
        )
          : (<></>)
      }
    </div>
  );
}

Basket.propTypes = {
  basketItems: PropTypes.array,
  producerId: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  basketItems: state.BasketReducer.offers,
  producerId: state.BasketReducer.producerId,
});

export default connect(mapStateToProps)(withRouter(Basket));

const SummaryAlert = ({ text }) => {
  const [showing, setShowing] = useState(true);
  if (showing) {
    return (
      <div className="summary-alert">
        <p>{text}</p>
        <span onClick={() => setShowing(false)}>
          <FiX size={18} color="#eee" strokeWidth="2" />
        </span>
      </div>
    );
  }

  return <></>;
};
